import React, { useRef, useEffect } from "react";
import Box from "@mui/material/Box";
import clsx from "clsx";
import { useStyles } from "./educationalContentStyles";
import { IEducationalContent } from "./IEducationalContent";
import {
    aniEducationalContent,
    aniEducationalContentKill,
} from "./educationalContentAnimations";
import Link from "@mui/material/Link";

const isVideo = (url: string): boolean => {
    if (!url) {
        return false;
    }
    const regex = /.+\.(mp4|webm|avi|mov|mkv|mts)$/;
    if (url.match(regex)) {
        return true;
    }
    return false;
};

const EducationalContent = ({
    variant,
    tagline,
    description,
    link,
    assets,
    id,
}: IEducationalContent) => {
    const { classes } = useStyles();
    const root = useRef(null);

    useEffect(() => {
        if (root && variant) {
            aniEducationalContent(root, variant);
        } else {
            console.error("EducationalContent: Missing variant");
        }

        return () => {
            root && aniEducationalContentKill(root);
        };
    }, [variant]);

    return (
        <Box
            id={id}
            data-name="EducationalContent"
            ref={root}
            className={clsx([classes.root, variant])}
        >
            <Box className={clsx([classes.column, "left"])}>
                {variant === "image_double" && (
                    <Box className={classes.leftCopy}>
                        <img
                            id="column-left-image"
                            src={`${assets?.[0]}`}
                            alt="Educational 1"
                        />
                        <span
                            id="column-left-heading"
                            className={clsx([classes.heading, variant])}
                        >
                            {tagline}
                        </span>
                        <span
                            id="column-left-description"
                            className={clsx([classes.description, variant])}
                        >
                            {description}
                        </span>
                    </Box>
                )}
                {variant === "image_single" && (
                    <Box
                        id="left-image"
                        className={classes.leftImage}
                        sx={{
                            backgroundImage: `url(${assets?.[0]})`,
                        }}
                    />
                )}
                {variant === "video" && (
                    <Box id="v2" className={clsx([classes.leftCopy, variant])}>
                        <span
                            id="column-left-heading"
                            className={clsx([classes.heading, variant])}
                        >
                            {tagline}
                        </span>
                        <span
                            id="column-left-description"
                            className={clsx([classes.description, variant])}
                        >
                            {description}
                        </span>
                    </Box>
                )}
            </Box>
            <Box className={clsx([classes.column, "right"])}>
                {variant === "image_double" && (
                    <Box className={classes.rightImage}>
                        <img
                            id="column-right-image"
                            src={`${assets?.[1]}`}
                            alt="Educational 2"
                        />
                    </Box>
                )}
                {variant === "image_single" && (
                    <Box
                        className={classes.rightCopy}
                        py={8}
                        display="flex"
                        alignSelf={"flex-end"}
                    >
                        <span
                            id="left-heading"
                            className={clsx([classes.heading, "right"])}
                        >
                            {tagline}
                        </span>
                        <span
                            id="left-description"
                            className={clsx([classes.description, "right"])}
                        >
                            {description}
                        </span>
                        <div>
                            {!!link && (
                                <Link
                                    id="left-cta"
                                    href={link}
                                    className={classes.cta}
                                >
                                    Learn More
                                </Link>
                            )}
                        </div>
                    </Box>
                )}
            </Box>

            {variant === "video" && (
                <Box className={classes.backgroundAsset}>
                    {isVideo(assets?.[0]!) ? (
                        <video
                            src={assets?.[0]}
                            autoPlay
                            aria-hidden
                            muted
                            loop
                        >
                            Your browser does not support the video element.
                        </video>
                    ) : (
                        <img
                            src={`${assets?.[0]}`}
                            alt="Educational content background"
                            aria-hidden
                        />
                    )}
                </Box>
            )}
        </Box>
    );
};

export default EducationalContent;
